import { useCallback, useEffect, useState } from 'react';
import { CompanyDailyNavConfigVersion, FirmDailyNavConfigVersion } from 'api';

type Change<T> = { before: T | null; after: T };

export type DailyNavSetupSessionChanges = {
  firm: Change<FirmDailyNavConfigVersion> | null;
  companies: Record<number, Change<CompanyDailyNavConfigVersion> & { companyName: string }>;
};

const EMPTY: DailyNavSetupSessionChanges = { firm: null, companies: {} };

const storageKey = (firmId: number) => `daily-nav-setup-changes-${firmId}`;

const read = (firmId: number): DailyNavSetupSessionChanges => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(firmId));
    return raw ? (JSON.parse(raw) as DailyNavSetupSessionChanges) : EMPTY;
  } catch {
    return EMPTY;
  }
};

// What this visit to the wizard saved, for Review. Per tab, so it survives a reload
// but not a new session; a repeat save keeps the first `before`.
export const useSetupSessionChanges = (firmId: number | null) => {
  const [changes, setChanges] = useState<DailyNavSetupSessionChanges>(EMPTY);

  useEffect(() => {
    setChanges(firmId ? read(firmId) : EMPTY);
  }, [firmId]);

  const persist = useCallback(
    (updater: (current: DailyNavSetupSessionChanges) => DailyNavSetupSessionChanges) => {
      setChanges(current => {
        const next = updater(current);
        try {
          if (firmId) {
            window.sessionStorage.setItem(storageKey(firmId), JSON.stringify(next));
          }
        } catch {
          // Storage unavailable: Review still shows this render's changes.
        }
        return next;
      });
    },
    [firmId]
  );

  const recordFirmSave = useCallback(
    (before: FirmDailyNavConfigVersion | null, after: FirmDailyNavConfigVersion) =>
      persist(current => ({ ...current, firm: { before: current.firm ? current.firm.before : before, after } })),
    [persist]
  );

  const recordCompanySave = useCallback(
    (companyId: number, companyName: string, before: CompanyDailyNavConfigVersion | null, after: CompanyDailyNavConfigVersion) =>
      persist(current => ({
        ...current,
        companies: {
          ...current.companies,
          [companyId]: {
            companyName,
            before: current.companies[companyId] ? current.companies[companyId].before : before,
            after,
          },
        },
      })),
    [persist]
  );

  return { changes, recordFirmSave, recordCompanySave };
};
